import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, NgZone, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { PaymentMethodService } from '@rma/billing/public/api-payment-methods';
import { PaymentMethodAlertStatus } from '@rma/billing-api';
import { StandardErrorMessage } from '@rma/generic/domain/to-be-sorted';
import { StripeModule, StripeService } from '@rma/generic/orc/stripe';
import { CalloutComponent } from '@rma/generic/ui/callout';
import { MatErrorOverrideComponent } from '@rma/generic/ui/form-error';
import { HeapCustomEventType, HeapEventService } from '@rma/generic/util/heap';
import { SetupIntentResult, StripeElements, StripeError, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { PaymentMethodAlertService } from '../payment-method-alert.service';

@Component({
  selector: 'rma-payment-method-alert-dialog',
  templateUrl: './payment-method-alert-dialog.component.html',
  styleUrls: ['./payment-method-alert-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CalloutComponent,
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatErrorOverrideComponent,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    StripeModule,
    TranslocoModule,
  ],
  providers: [provideTranslocoScope('subscriptions')],
})
export class PaymentMethodAlertDialogComponent implements OnInit {
  public PaymentMethodAlertStatus = PaymentMethodAlertStatus;

  public isSubmitting: boolean;
  public isComplete: boolean;
  public form: UntypedFormGroup;
  public clientSecret$?: Observable<string | null>;

  public error?: string;

  private stripeElements: StripeElements;

  constructor(
    @Inject(MAT_DIALOG_DATA) public status: PaymentMethodAlertStatus,
    private readonly dialogRef: MatDialogRef<PaymentMethodAlertDialogComponent>,
    private readonly paymentMethodAlertService: PaymentMethodAlertService,
    private readonly paymentMethodService: PaymentMethodService,
    private readonly heapEventService: HeapEventService,
    private readonly stripeService: StripeService,
    private readonly zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.clientSecret$ = this.paymentMethodService.setupIntent().pipe(
      catchError((x) => {
        this.error = x.message ?? StandardErrorMessage;
        return of(null);
      }),
    );
    this.heapEventService.trackCustomEvent(HeapCustomEventType.Dialog, { dialog: 'PaymentMethodAlert' });
  }

  public onStripeElementLoaded(stripeElements: StripeElements) {
    this.stripeElements = stripeElements;
  }

  public onStripeElementChanged($event: StripePaymentElementChangeEvent): void {
    this.isComplete = $event.complete;
  }

  public remindMe() {
    this.zone.run(() => {
      this.paymentMethodAlertService.delayAlert(2);
      this.dialogRef.close();
    });
  }

  public onSubmit() {
    this.isSubmitting = true;

    this.stripeService
      .confirmSetup(this.stripeElements, `${location.origin}/dashboard/manage-account/billing?is_default=true`)
      .pipe(
        map((stripeResponse: SetupIntentResult) => {
          if (stripeResponse.error) {
            throw stripeResponse.error;
          }
          return stripeResponse.setupIntent;
        }),
        switchMap((x) =>
          x.status === 'succeeded' && x.payment_method
            ? this.paymentMethodService.setDefaultPaymentMethod(x.payment_method as string).pipe(catchError(() => of(null)))
            : of(null),
        ),
      )
      .subscribe(
        (_) => {
          this.zone.run(() => {
            this.paymentMethodAlertService.delayAlert(5);
            this.dialogRef.close();
          });
        },
        (err: StripeError) => {
          this.isSubmitting = false;
          this.error = err.message ?? StandardErrorMessage;
        },
      );
  }
}
